/**
 * Occupation Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */

import React from 'react';
import { InlineFilterBar, InlineMultiSelect, InlineSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { useCompanyContext } from '../../../hooks/useCompanyContext';
import { SPECIAL_ACTIVITIES, SpecialActivityType } from '../../../services/occupationService';

// Types
interface OccupationFiltersData {
  searchTerm: string;
  companyId: string;
  departmentId: string;
  specialActivities: SpecialActivityType[];
}

interface OccupationFiltersProps {
  onFilter: (filters: OccupationFiltersData) => void;
  loading?: boolean;
  total?: number;
  companies?: Array<{ id: number; tradeName: string; legalName: string }>;
  departments?: Array<{ id: number; name: string; companyId?: number }>;
}

// Options
const ACTIVITY_OPTIONS = SPECIAL_ACTIVITIES.map((a) => ({ value: a.value, label: a.label }));

const OccupationFilters: React.FC<OccupationFiltersProps> = ({
  onFilter,
  loading = false,
  total,
  companies = [],
  departments = [],
}) => {
  const { shouldShowCompanyField, effectiveCompanyId } = useCompanyContext();

  // When the company field is hidden (single-company user or header selection),
  // the filter is pinned to that company instead of exposing the control.
  const defaultCompanyId =
    !shouldShowCompanyField && effectiveCompanyId ? effectiveCompanyId.toString() : '';

  const defaultValues: OccupationFiltersData = {
    searchTerm: '',
    companyId: defaultCompanyId,
    departmentId: '',
    specialActivities: [],
  };

  const { filters, setFilter, setFilters, clearAll, activeChips } = useFilterState(
    defaultValues,
    {
      onFilter,
      live: true,
      labels: {
        departmentId: 'Setor',
        specialActivities: 'Atividades',
      },
      formatValue: {
        departmentId: (v: string) => departments.find((d) => d.id.toString() === v)?.name ?? v,
        specialActivities: (v: SpecialActivityType[]) =>
          v
            .map((value) => SPECIAL_ACTIVITIES.find((a) => a.value === value)?.label ?? value)
            .join(', '),
      },
    },
  );

  const COMPANY_OPTIONS = companies.map((c) => ({
    value: c.id.toString(),
    label: c.tradeName || c.legalName,
  }));

  const filteredDepartments = filters.companyId
    ? departments.filter((d) => d.companyId?.toString() === filters.companyId)
    : departments;

  const DEPARTMENT_OPTIONS = filteredDepartments.map((d) => ({
    value: d.id.toString(),
    label: d.name,
  }));

  // Changing the company invalidates the previously selected department, so
  // both keys must be committed together. setFilter only updates one key at a
  // time (it reads the previous state from closure), so a manual merge is
  // used here instead of two consecutive setFilter calls.
  const handleCompanyChange = (value: string) => {
    const next: OccupationFiltersData = { ...filters, companyId: value, departmentId: '' };
    setFilters({ companyId: value, departmentId: '' });
    onFilter(next);
  };

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome ou CBO..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="cargos"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      {shouldShowCompanyField && (
        <InlineSelect
          label="Empresa"
          value={filters.companyId}
          options={COMPANY_OPTIONS}
          onChange={handleCompanyChange}
          emptyLabel="Todas"
          minWidth={180}
        />
      )}
      <InlineSelect
        label="Setor"
        value={filters.departmentId}
        options={DEPARTMENT_OPTIONS}
        onChange={(v) => setFilter('departmentId', v)}
        emptyLabel="Todos"
        disabled={!filters.companyId}
        minWidth={160}
      />
      <InlineMultiSelect
        label="Atividades Especiais"
        value={filters.specialActivities}
        options={ACTIVITY_OPTIONS}
        onChange={(v) => setFilter('specialActivities', v as SpecialActivityType[])}
        minWidth={190}
      />
    </InlineFilterBar>
  );
};

export default OccupationFilters;
